// src/components/presence/PresenceProvider.tsx — SCE Issues
// « Qui est connecté » : un battement par minute pour toute l'application.
//
// Chaque appli ouverte signale sa page dans SCE_UserPrefs puis relit qui
// d'autre est actif (cf. signalerPresence / listerPresents). Un seul
// Provider, monté une fois : la pile d'avatars et la mention « consulte aussi
// cette fiche » lisent le même état, sans doubler les requêtes.
//
// - Onglet caché : pas de battement. Qui laisse l'appli en arrière-plan
//   disparaît de la liste au bout de la fenêtre de présence.
// - Identité : le compte MSAL RÉEL, pas le profil affiché par la bascule de rôle (Lot 7) —
//   sinon on écrirait dans la ligne d'un autre. Un compte secondaire (téléphone
//   perso d'un salarié) est ramené à la personne qu'il représente
//   (auth/identiteCompte) : il est signalé sous son nom, dans SA ligne.
// - Guests B2B : ni signalés, ni destinataires. Savoir qui travaille dans
//   l'usine n'est pas de leur périmètre.
// - « Apparaître non connecté » (26/10, utils/presence) : plus de battement,
//   le dernier est effacé ; on voit toujours qui est là. Réglage du compte réel,
//   réservé à l'administrateur (Guillaume) : pour les autres, ni lecture du
//   réglage ni effet, toujours visibles.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { identiteDuCompte } from '@/auth/identiteCompte'
import { estAdmin } from '@/config/admin.config'
import {
  effacerPresence,
  ecrireBlocPrefs,
  lireBlocPrefs,
  listerPresents,
  signalerPresence,
  type Present,
} from '@/api/userPrefs.api'
import { CLE_BLOC_PRESENCE, cleCopieLocale, doitSignaler, lireReglagePresence } from '@/utils/presence'

const PERIODE_MS = 60_000
/** Une navigation n'est signalée qu'après ce délai de stabilité. */
const DELAI_PAGE_MS = 2_000

interface ContextePresence {
  /** Les autres personnes actives — jamais soi-même. */
  presents: Present[]
  /** « Apparaître non connecté » est proposé : compte réel administrateur. */
  disponible: boolean
  /** « Apparaître non connecté » est activé. */
  invisible: boolean
  changerInvisible: (invisible: boolean) => Promise<void>
}

const Contexte = createContext<ContextePresence>({
  presents: [],
  disponible: false,
  invisible: false,
  changerInvisible: async () => undefined,
})

function lireCopie(upn: string): boolean | null {
  if (!upn) return null
  try {
    const v = localStorage.getItem(cleCopieLocale(upn))
    return v === null ? null : v === '1'
  } catch {
    return null
  }
}

function ecrireCopie(upn: string, invisible: boolean): void {
  try {
    localStorage.setItem(cleCopieLocale(upn), invisible ? '1' : '0')
  } catch {
    // stockage bloqué : SharePoint garde le réglage
  }
}

export function usePresence(): ContextePresence {
  return useContext(Contexte)
}

export function PresenceProvider({ children }: { children: ReactNode }): JSX.Element {
  const { account } = useAuth()
  const location = useLocation()
  const [presents, setPresents] = useState<Present[]>([])

  const upn = identiteDuCompte(account)?.upn ?? ''
  const actif = upn !== '' && !upn.includes('#EXT#')
  const page = location.pathname
  const pageRef = useRef(page)
  pageRef.current = page

  const peutMasquer = actif && estAdmin(upn)
  // null tant que le réglage n'est connu ni par la copie locale ni par SharePoint :
  // aucun battement ne part dans ce cas. Hors administrateur : visible d'office.
  const [invisible, setInvisible] = useState<boolean | null>(() => (estAdmin(upn) ? lireCopie(upn) : false))
  const invisibleRef = useRef(invisible)
  invisibleRef.current = invisible
  /** Un choix fait pendant la lecture l'emporte sur ce qu'elle rapporte. */
  const choisiRef = useRef(false)

  useEffect(() => {
    if (!actif) return
    if (!peutMasquer) {
      setInvisible(false)
      return
    }
    let vivant = true
    choisiRef.current = false
    setInvisible(lireCopie(upn))
    lireBlocPrefs(upn, CLE_BLOC_PRESENCE)
      .then((brut) => {
        if (!vivant || choisiRef.current) return
        const v = lireReglagePresence(brut).invisible
        setInvisible(v)
        ecrireCopie(upn, v)
      })
      // Réglage illisible : visible, comme avant l'existence du réglage.
      .catch(() => vivant && setInvisible((x) => x ?? false))
    return () => {
      vivant = false
    }
  }, [actif, peutMasquer, upn])

  const battement = useCallback(async (): Promise<void> => {
    if (!actif || document.hidden) return
    try {
      if (doitSignaler(invisibleRef.current)) await signalerPresence(upn, pageRef.current)
      const liste = await listerPresents()
      const moi = upn.toLowerCase()
      setPresents(liste.filter((p) => p.upn.toLowerCase() !== moi))
    } catch (err) {
      // La présence est un confort : une panne ne doit rien bloquer.
      console.warn('[presence] battement échoué :', err)
    }
  }, [actif, upn])

  useEffect(() => {
    if (!actif) return
    const minuterie = setInterval(() => void battement(), PERIODE_MS)
    const auRetour = (): void => {
      if (!document.hidden) void battement()
    }
    document.addEventListener('visibilitychange', auRetour)
    return () => {
      clearInterval(minuterie)
      document.removeEventListener('visibilitychange', auRetour)
    }
  }, [actif, battement])

  // Au démarrage et à chaque changement de page, après un court délai : une
  // chaîne de fiches parcourue vite ne déclenche qu'une écriture.
  useEffect(() => {
    if (!actif) return
    const attente = setTimeout(() => void battement(), DELAI_PAGE_MS)
    return () => clearTimeout(attente)
  }, [page, actif, battement])

  // Premier passage sans copie locale : le réglage arrive de SharePoint après le
  // battement de démarrage ; visible, on se signale sans attendre la minute suivante.
  const connuRef = useRef(invisible !== null)
  useEffect(() => {
    if (invisible === null || connuRef.current) return
    connuRef.current = true
    if (invisible === false) void battement()
  }, [invisible, battement])

  const changerInvisible = useCallback(
    async (v: boolean): Promise<void> => {
      if (!peutMasquer) return
      choisiRef.current = true
      invisibleRef.current = v
      setInvisible(v)
      ecrireCopie(upn, v)
      try {
        // Disparaître tout de suite des listes des autres, sans attendre 3 min.
        if (v) await effacerPresence(upn)
        await ecrireBlocPrefs(upn, CLE_BLOC_PRESENCE, { invisible: v })
      } catch (err) {
        // La copie locale tient le réglage sur cet appareil ; SharePoint au prochain essai.
        console.warn('[presence] réglage non enregistré :', err)
      }
      if (!v) void battement()
    },
    [peutMasquer, upn, battement],
  )

  return (
    <Contexte.Provider value={{ presents, disponible: peutMasquer, invisible: invisible === true, changerInvisible }}>
      {children}
    </Contexte.Provider>
  )
}

/** Libellé lisible d'un chemin, pour dire OÙ est quelqu'un. */
export function libellePage(chemin: string): string {
  const [, racine = '', suite] = chemin.split('/')
  const id = suite ? decodeURIComponent(suite) : ''
  switch (racine) {
    case '':
      return 'Accueil'
    case 'issues':
      return id ? `Problème ${id}` : 'Liste des problèmes'
    case 'new':
      return 'Déclaration'
    case 'my-issues':
      return 'Mes déclarations'
    case 'dashboard':
      return 'Statistiques'
    case 'equipements':
      return id ? `Équipement ${id}` : 'Équipements'
    case 'projets':
      return id ? `Projet ${id}` : 'Projets'
    case 'admin':
      return 'Administration'
    default:
      return 'Ailleurs dans l’appli'
  }
}

/** « à l'instant », « il y a 2 min ». */
export function depuisQuand(iso: string, maintenant: number = Date.now()): string {
  const minutes = Math.floor((maintenant - Date.parse(iso)) / 60_000)
  return minutes < 1 ? 'à l’instant' : `il y a ${minutes} min`
}
